/**
 * POSPage - Point of Sale Terminal for Counter Staff (Refactored)
 * Features:
 * - 6-digit PIN staff authentication
 * - Customer lookup by phone/email
 * - Full menu browsing with cart
 * - Auto-applied loyalty discounts
 * - Receipt printing and cash drawer integration
 * - Light/Dark mode support
 * - Full item customization support
 * - Upsell reminders for servers
 * - REAL-TIME MENU SYNC: Auto-refreshes when admin makes changes
 * 
 * Refactored from 3000+ lines by importing sub-components and custom hooks:
 * - POSTopBar, POSOrderCompleteScreen (display components)
 * - POSPinPad, POSCustomerLookup, POSNewCustomerForm (auth/customer)
 * - POSCart, POSMenuGrid, POSQuickActions (ordering)
 * - usePOSCart (cart state management)
 * - usePOSParkedSales (parked sales persistence)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Coffee, X, Bell, Check, GraduationCap,
  Loader2, ChevronDown, ChevronUp, Smartphone,
  Gift, Clock, DollarSign, CreditCard, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { useTheme } from '@/context/ThemeContext';
import ItemCustomizationModal from '@/components/ordering/ItemCustomizationModal';
import { useFiscalInvoice, printFiscalReceipt } from '@/components/frcs/FiscalReceipt';
import { triggerUSBPrint } from '@/utils/usbPrint';
import { useMenuSync } from '@/hooks/useMenuSync';

// Import custom hooks for cart and parked sales
import { usePOSCart } from '@/hooks/usePOSCart';
import { usePOSParkedSales } from '@/hooks/usePOSParkedSales';

// Import order utilities for payment processing
import { 
  buildOrderPayload, 
  extractErrorMessage,
  buildDiscountLabel as getDiscountLabel,
  formatCartItemForKitchen
} from '@/utils/posOrderUtils';

// Import refactored sub-components
import {
  POSPinPad,
  POSCustomerLookup,
  POSNewCustomerForm,
  POSCart,
  POSMenuGrid,
  POSQuickActions,
  POSRefundFlow,
  POSVoidInvoice,
  useTrainingMode,
  POSOrderTypeSelector,
  POSLogMistake
} from '@/components/pos';
import POSTopBar from '@/components/pos/POSTopBar';
import POSOrderCompleteScreen from '@/components/pos/POSOrderCompleteScreen';
import POSMobileCartDrawer from '@/components/pos/POSMobileCartDrawer';
import POSTableBillLookup from '@/components/pos/POSTableBillLookup';
import POSStaffDiscount from '@/components/pos/POSStaffDiscount';
import POSPromoCode from '@/components/pos/POSPromoCode';
import POSFamilyFriendsDiscount from '@/components/pos/POSFamilyFriendsDiscount';
import POSParkedSalesModal from '@/components/pos/POSParkedSalesModal';
import POSActionBar from '@/components/pos/POSActionBar';
import POSCashierDiscountModal from '@/components/pos/POSCashierDiscountModal';
import POSOrderHistory from '@/components/pos/POSOrderHistory';
import POSIncomingOrders from '@/components/pos/POSIncomingOrders';
import POSPrintFailureAlert from '@/components/pos/POSPrintFailureAlert';
import { 
  useShiftManager, 
  POSOpenShiftPrompt, 
  POSCloseShiftDialog
} from '@/components/pos/POSShiftManager';


// Main POS Terminal Component
export default function POSPage() {
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // Auth State - Load from localStorage for session persistence across refreshes
  const [staff, setStaff] = useState(() => {
    try {
      const savedStaff = localStorage.getItem('pos_staff_session');
      return savedStaff ? JSON.parse(savedStaff) : null;
    } catch {
      return null;
    }
  });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // View State - Restore view based on saved session
  const [view, setView] = useState(() => {
    try {
      const savedStaff = localStorage.getItem('pos_staff_session');
      return savedStaff ? 'customer-lookup' : 'login';
    } catch {
      return 'login';
    }
  });
  const [customer, setCustomer] = useState(null);
  const [customerDiscount, setCustomerDiscount] = useState(null);
  
  // Cashier Discount State
  const [appliedCashierDiscount, setAppliedCashierDiscount] = useState(null);
  
  // Menu State
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Cart State - Using usePOSCart hook for cart management
  const {
    cart,
    setCart,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart: clearCartOnly,
    restoreCart,
    isEmpty: isCartEmpty,
    itemCount: cartItemCount
  } = usePOSCart([]);
  
  // Extended clearCart that also resets related state
  const clearCart = useCallback(() => {
    clearCartOnly();
    setAppliedCashierDiscount(null);
    setTableOrderIds(null);
  }, [clearCartOnly]);
  
  // Payment State
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [orderProcessing, setOrderProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  
  // Quick Actions State
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [quickActionLoading, setQuickActionLoading] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  
  // Customization modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCustomization, setShowCustomization] = useState(false);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  
  // Kiosk queue state
  const [pendingKioskOrders, setPendingKioskOrders] = useState([]);
  const [paidKioskOrders, setPaidKioskOrders] = useState([]);  // M-PAiSA paid, pending confirmation
  
  // Kiosk Cash Checkout Modal State
  const [kioskCheckoutOrder, setKioskCheckoutOrder] = useState(null);
  const [kioskCashReceived, setKioskCashReceived] = useState('');
  const [kioskCheckoutProcessing, setKioskCheckoutProcessing] = useState(false);
  
  // Mobile cart drawer state
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [showKioskQueue, setShowKioskQueue] = useState(false);
  
  // Upsell reminder state
  const [upsellReminderCollapsed, setUpsellReminderCollapsed] = useState(false);

  // Order Type State (Phase 10: Takeaway vs Dine-in)
  const [orderType, setOrderType] = useState('takeaway');
  const [tableNumber, setTableNumber] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);

  // FRCS Fiscal Invoice Hook
  const { fiscalInvoice, fiscalizeItems, clearFiscalInvoice } = useFiscalInvoice();
  const [showFiscalReceipt, setShowFiscalReceipt] = useState(false);

  // Special screens state
  const [showRefundFlow, setShowRefundFlow] = useState(false);
  const [showVoidInvoice, setShowVoidInvoice] = useState(false);

  // Training Mode
  const { isTrainingMode, setIsTrainingMode, getInvoiceType } = useTrainingMode();
  
  // Owner Order Mode - orders that don't charge but print to stations
  const [isOwnerOrder, setIsOwnerOrder] = useState(false);

  // Shift Management State
  const { activeShift, loading: shiftLoading, refreshShift } = useShiftManager(staff?.id);
  const [showCloseShiftDialog, setShowCloseShiftDialog] = useState(false);

  // M-PAiSA Payment State
  const [showMpaisaPayment, setShowMpaisaPayment] = useState(false);
  const [mpaisaPaymentUrl, setMpaisaPaymentUrl] = useState(null);
  const [mpaisaOrderId, setMpaisaOrderId] = useState(null);

  // Parked Sales State - Using custom hook with UI state wrapper
  // Core persistence logic is in usePOSParkedSales hook
  const parkedSalesHook = usePOSParkedSales(staff?.id);
  const [showParkedSales, setShowParkedSales] = useState(false);
  const [mpaisaPolling, setMpaisaPolling] = useState(false);
  
  // Expose hook values with existing variable names for backward compatibility
  const parkedSales = parkedSalesHook.parkedSales;
  const parkedSalesLoading = parkedSalesHook.loading;
  const currentParkedSaleId = parkedSalesHook.currentParkedSaleId;
  const setCurrentParkedSaleId = parkedSalesHook.setCurrentParkedSaleId;
  const fetchParkedSales = parkedSalesHook.fetchParkedSales;

  // New POS Feature States
  const [showTableBillLookup, setShowTableBillLookup] = useState(false);
  const [showStaffOrder, setShowStaffOrder] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [showLogMistake, setShowLogMistake] = useState(false);
  const [tableOrderIds, setTableOrderIds] = useState(null); // Track existing table order IDs when adding items
  
  // Family & Friends Discount State
  const [showFFDiscount, setShowFFDiscount] = useState(false);
  const [appliedFFDiscount, setAppliedFFDiscount] = useState(null);
  
  // Cashier Discount Modal State
  const [showCashierDiscountModal, setShowCashierDiscountModal] = useState(false);

  // Coffee Pass State
  const [customerCoffeePass, setCustomerCoffeePass] = useState(null);

  // Order History Modal State (2-day lookback)
  const [showOrderHistory, setShowOrderHistory] = useState(false);

  // ===== SESSION PERSISTENCE =====
  // Persist staff session to localStorage when it changes
  useEffect(() => {
    if (staff) {
      localStorage.setItem('pos_staff_session', JSON.stringify(staff));
    } else {
      localStorage.removeItem('pos_staff_session');
    }
  }, [staff]);

  // Validate saved session on mount (check if staff still exists in backend)
  useEffect(() => {
    const validateSession = async () => {
      const savedStaff = localStorage.getItem('pos_staff_session');
      if (savedStaff) {
        try {
          const staffData = JSON.parse(savedStaff);
          // Verify the staff is still valid by re-authenticating with PIN
          if (staffData.pin) {
            const res = await api.post('/api/device/login', { pin: staffData.pin, device_type: 'pos' });
            if (res.data?.staff) {
              setStaff({ ...res.data.staff, pin: staffData.pin });
              setView('customer-lookup');
              return;
            }
          }
        } catch (err) {
          console.warn('Session validation failed, clearing saved session');
        }
        // Clear invalid session
        localStorage.removeItem('pos_staff_session');
        setStaff(null);
        setView('login');
      }
    };
    validateSession();
  }, []);

  // Helper function to get discount label for receipt printing
  const getDiscountLabel = (cashierDiscount, loyaltyDiscount, customerDiscountInfo) => {
    if (cashierDiscount && (cashierDiscount.percent > 0 || cashierDiscount.amount > 0)) {
      const percent = typeof cashierDiscount.percent === 'number' ? cashierDiscount.percent : 0;
      switch (cashierDiscount.type) {
        case 'staff_50':
          return `Staff 50% (${cashierDiscount.staffName || 'Staff'})`;
        case 'family_friends':
          return `Family & Friends ${percent}%`;
        case 'cashier':
          return `Manager Discount ${percent}%`;
        case 'manual':
          return `Manual Discount ${percent}%`;
        case 'promo':
          return `Promo Code ${cashierDiscount.code || ''} ${percent ? `${percent}%` : ''}`.trim();
        default:
          return percent > 0 ? `Discount ${percent}%` : 'Discount Applied';
      }
    }
    if (loyaltyDiscount > 0 && customerDiscountInfo) {
      const tier = customerDiscountInfo.tier || 'Member';
      return `Loyalty ${tier} ${loyaltyDiscount}%`;
    }
    return null;
  };

  // Sync cart to Customer Facing Display via localStorage
  useEffect(() => {
    // Calculate totals from cart
    const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
    // VAT is INCLUDED in all prices (12.5% Fiji VAT) - extract it for display, don't add it
    const cartTax = cartSubtotal * 0.125 / (1 + 0.125); // Extract VAT from inclusive price
    const cartTotal = cartSubtotal; // Total is the price as displayed (VAT already included)
    const currentCustomerName = customer?.name || customer?.first_name || '';
    
    const displayData = {
      items: cart.map(item => ({
        id: item.item_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity || 1,
        modifiers: item.customizations || []
      })),
      subtotal: cartSubtotal,
      tax: cartTax,
      total: cartTotal,
      discount: appliedCashierDiscount?.percent || customerDiscount?.discount || 0,
      discount_name: appliedCashierDiscount?.name || customerDiscount?.name || '',
      customer_name: currentCustomerName,
      order_type: orderType,
      status: cart.length > 0 ? 'ordering' : 'idle',
      timestamp: Date.now()
    };
    localStorage.setItem('pos_customer_display', JSON.stringify(displayData));
    
    // Also send to API for separate device sync
    try {
      api.post('/pos/customer-display/update', {
        station_id: 'pos-main',
        items: displayData.items,
        subtotal: displayData.subtotal,
        discount_amount: displayData.discount || 0,
        discount_name: displayData.discount_name || '',
        tax: displayData.tax,
        total: displayData.total,
        order_type: displayData.order_type,
        customer_name: displayData.customer_name
      }).catch(() => {}); // Silently fail if API unavailable
    } catch (e) {
      // Ignore API errors
    }
  }, [cart, customer, appliedCashierDiscount, customerDiscount, orderType]);

  // Send "Thank You" to customer display after successful payment
  const sendThankYouToDisplay = useCallback((orderTotal) => {
    const currentCustomerName = customer?.name || customer?.first_name || '';
    const displayData = {
      items: [],
      subtotal: 0,
      tax: 0,
      total: orderTotal || 0,
      discount: 0,
      customer_name: currentCustomerName,
      status: 'thankyou',
      timestamp: Date.now()
    };
    localStorage.setItem('pos_customer_display', JSON.stringify(displayData));
    
    // Also send to API for separate device sync
    try {
      api.post('/pos/customer-display/payment-complete', {
        station_id: 'pos-main',
        total: orderTotal || 0
      }).catch(() => {}); // Silently fail if API unavailable
    } catch (e) {
      // Ignore API errors
    }
  }, [customer]);

  // Fetch pending kiosk orders
  const fetchPendingKioskOrders = useCallback(async () => {
    try {
      // Fetch kiosk orders that need cash payment at cashier
      const cashRes = await api.get('/api/orders/kiosk-pending-cash');
      setPendingKioskOrders(cashRes.data.orders || []);
      
      // Fetch M-PAiSA paid orders pending confirmation
      const paidRes = await api.get('/api/orders/kiosk-pending-confirmation');
      setPaidKioskOrders(paidRes.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch kiosk orders:', err);
    }
  }, []);

  useEffect(() => {
    if (staff) {
      fetchPendingKioskOrders();
      const interval = setInterval(fetchPendingKioskOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [staff, fetchPendingKioskOrders]);

  // Fetch available tables for dine-in
  const fetchTables = useCallback(async () => {
    try {
      const res = await api.get('/api/tables/active');
      setAvailableTables(res.data.tables || []);
    } catch (err) {
      console.error('Failed to fetch tables:', err);
    }
  }, []);

  useEffect(() => {
    if (staff) {
      fetchTables();
    }
  }, [staff, fetchTables]);

  // Handle PIN login
  const handleLogin = async (pin) => {
    setLoginLoading(true);
    try {
      const res = await api.post('/api/device/login', { pin, device_type: 'pos' });
      const data = res.data;
      setStaff({ ...data.staff, pin }); // Store PIN for shift opening
      
      // Check for active shift
      try {
        const shiftRes = await api.get('/api/pos/shifts/active');
        if (shiftRes.data) {
          // Shift exists, go to customer lookup
          setView('customer-lookup');
        } else {
          // No shift, prompt to open one
          setView('open-shift');
        }
      } catch {
        // No shift found
        setView('open-shift');
      }
      
      toast.success(data.message);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Invalid PIN' };
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    if (staff) {
      try {
        await api.post(`/api/pos/logout?staff_id=${staff.id}`);
      } catch (err) {
        console.error('Logout error:', err);
      }
    }
    // Clear session from localStorage
    localStorage.removeItem('pos_staff_session');
    setStaff(null);
    setCustomer(null);
    setCustomerDiscount(null);
    setCart([]);
    setView('login');
  };

  // Fetch menu items
  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);
      const cacheBuster = `?_t=${Date.now()}`;
      const res = await api.get(`/menu${cacheBuster}`);
      const available = res.data.filter(item => item.is_available !== false);
      setMenuItems(available);
      const cats = [...new Set(available.map(item => item.category))].filter(Boolean);
      setCategories(cats);
      
      const addOnsRes = await api.get(`/add-ons${cacheBuster}`);
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh add-ons periodically (every 60 seconds) to pick up admin changes
  const refreshAddOns = useCallback(async () => {
    try {
      const cacheBuster = `?_t=${Date.now()}`;
      const addOnsRes = await api.get(`/add-ons${cacheBuster}`);
      setAvailableAddOns(addOnsRes.data.filter(a => a.is_available !== false));
    } catch (err) {
      console.error('Failed to refresh add-ons:', err);
    }
  }, []);

  // Auto-refresh add-ons every 60 seconds
  useEffect(() => {
    if (staff) {
      const interval = setInterval(refreshAddOns, 60000);
      return () => clearInterval(interval);
    }
  }, [staff, refreshAddOns]);

  // ============== REAL-TIME MENU SYNC ==============
  // Listen for menu changes from admin panel and auto-refresh
  const handleMenuUpdate = useCallback((data) => {
    console.log('[POS] Menu update received:', data);
    
    // Show a subtle toast notification
    const updateType = data.update_type || 'unknown';
    const itemName = data.data?.item_name;
    const action = data.data?.action || 'updated';
    
    let message = 'Menu updated';
    if (updateType === 'menu_item' && itemName) {
      message = `Menu item ${action}: ${itemName}`;
    } else if (updateType === 'add_on') {
      message = 'Add-ons updated';
    } else if (updateType === 'category') {
      message = 'Categories updated';
    } else if (updateType === 'full_refresh') {
      message = 'Full menu refresh';
    }
    
    toast.info(message, { 
      description: 'Refreshing menu...',
      duration: 2000 
    });
    
    // Refetch the menu data
    fetchMenu();
  }, [fetchMenu]);

  // Connect to menu sync WebSocket (only when staff is logged in)
  const { isConnected: menuSyncConnected } = useMenuSync(
    staff ? handleMenuUpdate : null
  );

  // ============== PARKED SALES - Using usePOSParkedSales Hook ==============
  // Note: fetchParkedSales, parkedSales, parkedSalesLoading, currentParkedSaleId 
  // are now provided by usePOSParkedSales hook (defined above in state section)
  // The hook handles initial fetch and auto-refresh internally

  // Fetch customer discount
  const fetchCustomerDiscount = async (cust) => {
    if (!cust?.email) return;
    try {
      const res = await api.get(`/api/customer-discount?email=${encodeURIComponent(cust.email)}`);
      const discountData = res.data;
      if (discountData.discount > 0) {
        setCustomerDiscount(discountData);
        toast.success(`${discountData.discount}% loyalty discount applied!`, {
          description: discountData.tier || discountData.reason
        });
      }
    } catch (err) {
      console.error('Failed to fetch discount:', err);
    }
  };

  // Fetch Coffee Pass status for a customer (called when customer selected without pass data)
  const fetchCoffeePassForCustomer = async (cust) => {
    try {
      const params = new URLSearchParams();
      if (cust.email) params.append('email', cust.email);
      if (cust.phone) params.append('phone', cust.phone);
      if (cust.contact_id) params.append('contact_id', cust.contact_id);
      
      const res = await api.get(`/coffee-pass/check?${params.toString()}`);
      if (res.data) {
        setCustomerCoffeePass(res.data);
        // Show toast based on pass status
        if (res.data.can_redeem_now) {
          toast.success('Bula Coffee Pass Ready!', {
            description: `${cust.first_name || 'Customer'} can redeem free coffee. Select any coffee from the menu!`,
            icon: <Coffee className="w-4 h-4" />,
            duration: 6000
          });
        } else if (res.data.can_upgrade_to_redeem) {
          toast.info(`Upgrade Available - $${res.data.upgrade_info?.upgrade_price || 40}`, {
            description: `${cust.first_name || 'Customer'} has a morning-only pass. Upgrade to unlock afternoon redemption!`,
            icon: <Crown className="w-4 h-4" />,
            duration: 8000
          });
        } else if (res.data.has_active_pass) {
          if (res.data.already_redeemed_today) {
            toast.info('Coffee Pass already redeemed today', {
              description: `${cust.first_name || 'Customer'} enjoyed their free coffee earlier today.`,
              icon: <Coffee className="w-4 h-4" />
            });
          } else {
            toast.info('Coffee Pass - Outside redemption hours', {
              description: res.data.window_message || 'Redemption available 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM',
              icon: <Clock className="w-4 h-4" />
            });
          }
        }
        return res.data;
      }
    } catch (err) {
      console.error('Failed to fetch coffee pass status:', err);
    }
    return null;
  };

  // Select customer
  const handleSelectCustomer = async (cust) => {
    setCustomer(cust);
    fetchCustomerDiscount(cust);
    
    // Check if customer has Coffee Pass info attached from lookup
    if (cust.coffeePass) {
      setCustomerCoffeePass(cust.coffeePass);
      // Show info toast about their pass status
      if (cust.coffeePass.can_redeem_now) {
        toast.success('Bula Coffee Pass Ready!', {
          description: `${cust.first_name || 'Customer'} can redeem free coffee. Select any coffee from the menu!`,
          icon: <Coffee className="w-4 h-4" />,
          duration: 6000
        });
      } else if (cust.coffeePass.can_upgrade_to_redeem) {
        // Standard pass holder in afternoon window - offer upgrade!
        toast.info(`Upgrade Available - $${cust.coffeePass.upgrade_info?.upgrade_price || 40}`, {
          description: `${cust.first_name || 'Customer'} has a morning-only pass. Upgrade to unlock afternoon redemption!`,
          icon: <Crown className="w-4 h-4" />,
          duration: 8000
        });
      } else if (cust.coffeePass.has_active_pass) {
        if (cust.coffeePass.already_redeemed_today) {
          toast.info('Coffee Pass already redeemed today', {
            description: `${cust.first_name || 'Customer'} enjoyed their free coffee earlier today.`,
            icon: <Coffee className="w-4 h-4" />
          });
        } else {
          toast.info('Coffee Pass - Outside redemption hours', {
            description: cust.coffeePass.window_message || 'Redemption available 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM',
            icon: <Clock className="w-4 h-4" />
          });
        }
      }
    } else {
      // Pass info not attached - fetch it now (handles race condition from lookup)
      setCustomerCoffeePass(null);
      // Check if customer might have a pass (based on contact tags or flags)
      if (cust.has_coffee_pass || cust.has_active_coffee_pass || cust.email || cust.phone) {
        await fetchCoffeePassForCustomer(cust);
      }
    }
    
    setView('ordering');
  };

  // Skip customer lookup (guest order)
  const handleSkipCustomer = () => {
    setCustomer(null);
    setCustomerDiscount(null);
    setCustomerCoffeePass(null);
    setView('ordering');
  };

  // Fetch menu on mount and when view changes to ordering - ALWAYS refresh for latest prices
  useEffect(() => {
    if (view === 'ordering') {
      fetchMenu(); // Always fetch fresh menu data with latest prices
    }
  }, [view, fetchMenu]);

  // Add item to cart (with order type validation)
  const handleItemClick = (item) => {
    // Require table selection for dine-in before adding items
    if (orderType === 'dine-in' && !tableNumber) {
      toast.error('Please select a table number first', {
        description: 'Dine-in orders require a table assignment'
      });
      return;
    }
    
    // Special handling for Bula Coffee Pass - skip customization
    if (item.is_coffee_pass || item.skip_customization) {
      addToCart(item, null, null, true);
      return;
    }
    
    // Open customization modal if item has sizes (even just 1 for upsell), add-ons, or other customizations
    if (item.has_customizations || item.sizes?.length > 0 || availableAddOns.length > 0) {
      setSelectedItem(item);
      setShowCustomization(true);
    } else {
      addToCart(item);
    }
  };

  // Note: addToCart, updateQuantity, removeItem are provided by usePOSCart hook
  // clearCart is wrapped above to also reset related state (discounts, table orders)

  // Park current sale - uses hook for server persistence
  const parkSale = async () => {
    const result = await parkedSalesHook.parkSale({
      cart,
      customer,
      customerDiscount,
      customerCoffeePass,
      appliedCashierDiscount,
      orderType,
      tableNumber
    });
    
    if (result.success) {
      // Clear local state after successful park
      setCart([]);
      setCustomer(null);
      setCustomerDiscount(null);
      setCustomerCoffeePass(null);
      setAppliedCashierDiscount(null);
      setTableNumber(null);
      setView('customer-lookup');
    }
  };

  // Resume a parked sale - uses hook for server interaction
  const resumeParkedSale = async (parkedSaleId) => {
    const result = await parkedSalesHook.resumeParkedSale(parkedSaleId);
    
    if (result.success) {
      // Restore cart and customer state
      const restoredCart = (result.cartItems || []).map(item => ({
        id: item.item_id || item.id || `item-${Date.now()}`,
        item_id: item.item_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity || 1,
        category: item.category,
        size: item.size,
        selectedSize: item.selectedSize,
        add_ons: item.add_ons || [],
        special_instructions: item.special_instructions,
        is_coffee_pass_redemption: item.is_coffee_pass_redemption || false
      }));
      
      setCart(restoredCart);
      setCustomer(result.customer);
      setCustomerDiscount(result.customerDiscount);
      setCustomerCoffeePass(result.customerCoffeePass);
      setAppliedCashierDiscount(result.cashierDiscount);
      setOrderType(result.orderType || 'takeaway');
      setTableNumber(result.tableNumber);
      
      setView('ordering');
      setShowParkedSales(false);
    }
  };

  // Release a parked sale - uses hook
  const releaseParkedSale = async (parkedSaleId) => {
    const result = await parkedSalesHook.releaseParkedSale(parkedSaleId);
    
    if (result.success && result.wasCurrentSale) {
      // Clear local state if we were working on this sale
      setCart([]);
      setCustomer(null);
      setCustomerDiscount(null);
      setCustomerCoffeePass(null);
      setAppliedCashierDiscount(null);
      setTableNumber(null);
      setView('customer-lookup');
    }
  };

  // Cancel/delete a parked sale - uses hook
  const deleteParkedSale = async (parkedSaleId, reason = 'Cancelled by staff') => {
    const result = await parkedSalesHook.deleteParkedSale(parkedSaleId, reason);
    
    if (result.success && result.wasCurrentSale) {
      // Clear local state if we were working on this sale
      setCart([]);
      setCustomer(null);
      setCustomerDiscount(null);
      setCustomerCoffeePass(null);
      setAppliedCashierDiscount(null);
      setTableNumber(null);
      setView('customer-lookup');
    }
  };

  // Mark parked sale as completed - uses hook
  const completeParkedSale = async (orderId) => {
    await parkedSalesHook.completeParkedSale(orderId);
  };

  // Go back to home (customer lookup)
  const goToHome = () => {
    if (cart.length > 0) {
      // Ask to park or discard
      const shouldPark = window.confirm('You have items in your cart. Park this sale for later?');
      if (shouldPark) {
        parkSale();
        return;
      }
    }
    // Clear everything and go to customer lookup
    setCart([]);
    setCustomer(null);
    setCustomerDiscount(null);
    setCustomerCoffeePass(null);
    setAppliedCashierDiscount(null);
    setTableNumber(null);
    setShowPayment(false);
    setView('customer-lookup');
  };


  // Process order
  const processOrder = async (mpaisaRefCode = null, cashData = null, splitPayments = null, partnerCode = null, eftposSurcharge = 0) => {
    if (cart.length === 0) return;
    
    // ========== DINE-IN FLOW ==========
    // For dine-in orders, use sendToKitchen (no payment yet)
    // This matches how Server tablet handles dine-in
    if (orderType === 'dine-in') {
      await sendToKitchen();
      return;
    }
    
    // ========== REGULAR POS FLOW (Takeaway/Paid) ==========
    setOrderProcessing(true);
    try {
      // Build order payload using utility function
      const orderData = buildOrderPayload({
        cart,
        customer,
        customerDiscount,
        appliedCashierDiscount,
        paymentMethod,
        splitPayments,
        mpaisaRefCode,
        staff,
        orderType,
        tableNumber,
        isTrainingMode,
        isOwnerOrder,
        cashData,
        partnerCode,
        eftposSurcharge,
        getInvoiceType
      });
      
      // Extract calculated values for local use
      const { total, isSplitPayment, finalPaymentMethod } = orderData._calculated;
      delete orderData._calculated; // Remove from payload sent to API
      
      console.log('Processing POS order:', JSON.stringify(orderData, null, 2));
      const res = await api.post('/api/pos/orders', orderData);
      const data = res.data;
      console.log('POS order response:', data);
      setLastOrder(data);
      
      // If M-PAiSA payment, create payment request and show QR/link
      if (paymentMethod === 'mpaisa') {
        try {
          const mpaisaRes = await api.post('/api/payments/mpaisa/create', {
            order_id: data.order_id,
            amount: total,
            description: `POS Order #${data.order_number}`,
            customer_phone: customer?.phone || '',
            customer_name: customer?.name || 'Walk-in Customer',
            source: 'pos'
          });
          
          const mpaisaData = mpaisaRes.data;
          
          if (mpaisaData.success && mpaisaData.payment_url) {
            setMpaisaPaymentUrl(mpaisaData.payment_url);
            setMpaisaOrderId(data.order_id);
            setShowMpaisaPayment(true);
            setCart([]);
            setShowPayment(false);
            setAppliedCashierDiscount(null);
            // Start polling for payment completion
            startMpaisaPolling(data.order_id);
          } else {
            toast.error('Failed to create M-PAiSA payment');
          }
        } catch (mpaisaErr) {
          console.error('M-PAiSA error:', mpaisaErr);
          toast.error('M-PAiSA payment setup failed');
        }
        setOrderProcessing(false);
        return;
      }
      
      // For M-PAiSA QR (static QR code) payments - reference code already recorded in order
      // Show success immediately as verification is manual (cashier sees customer SMS)
      if (paymentMethod === 'mpaisa_qr') {
        toast.success(`M-PAiSA payment recorded! Ref: ${mpaisaRefCode}`, {
          description: 'Reference code saved for reconciliation',
          duration: 4000
        });
        
        // Store order info for receipt confirmation
        const orderInfo = {
          order_id: data.order_id || data.id,
          order_number: data.order_number,
          total,
          fiscal_success: data.fiscal_success,
          sdc_number: data.sdc_number,
          is_training: isTrainingMode,
          payment_method: 'mpaisa_qr',
          mpaisa_ref_code: mpaisaRefCode
        };
        
        // Send Thank You to customer display
        sendThankYouToDisplay(total);
        
        // Mark parked sale as completed (if working on a parked sale)
        if (currentParkedSaleId) {
          await completeParkedSale(data.order_id || data.id);
        }
          
          setCart([]);
          setShowPayment(false);
          setAppliedCashierDiscount(null);
          
          // Generate fiscal invoice (background, don't block UI)
          if (!isTrainingMode) {
            fiscalizeItems(cart, total, 'mpaisa').catch(fiscalErr => {
              console.error('Fiscal error:', fiscalErr);
            });
          }
          
          // Go directly to order complete screen (no popup, no auto-print)
          // Cashier can reprint from Order History if customer wants receipt
          setOrderComplete(orderInfo);
          
          setOrderProcessing(false);
          return;
        }
        
        // For split payments, show summary success
        if (isSplitPayment && splitPayments) {
          const cashTotal = splitPayments.filter(p => p.method === 'cash').reduce((s, p) => s + p.amount, 0);
          const mpaisaTotal = splitPayments.filter(p => p.method !== 'cash').reduce((s, p) => s + p.amount, 0);
          
          const splitSummary = [];
          if (cashTotal > 0) splitSummary.push(`Cash $${cashTotal.toFixed(2)}`);
          if (mpaisaTotal > 0) splitSummary.push(`M-PAiSA/EFTPOS $${mpaisaTotal.toFixed(2)}`);
          
          toast.success(`Split Payment Complete!`, {
            description: splitSummary.join(' + '),
            duration: 5000
          });
          
          // Store order info for receipt confirmation
          const orderInfo = {
            order_id: data.order_id || data.id,
            order_number: data.order_number,
            total,
            fiscal_success: data.fiscal_success,
            sdc_number: data.sdc_number,
            is_training: isTrainingMode,
            payment_method: 'split',
            split_payments: splitPayments
          };
          
          // Store last order for reprint functionality
          setLastOrder({
            order_id: data.order_id || data.id,
            order_number: data.order_number,
            total,
            payment_method: 'split',
            split_payments: splitPayments
          });
          
          // Send Thank You to customer display
          sendThankYouToDisplay(total);
          
          // Mark parked sale as completed (if working on a parked sale)
          if (currentParkedSaleId) {
            await completeParkedSale(data.order_id || data.id);
          }
          
          setCart([]);
          setShowPayment(false);
          setAppliedCashierDiscount(null);
          
          // Generate fiscal invoice (background, don't block UI)
          if (!isTrainingMode) {
            fiscalizeItems(cart, total, 'split').catch(fiscalErr => {
              console.error('Fiscal error:', fiscalErr);
            });
          }
          
          // Go directly to order complete screen (no popup, no auto-print)
          // Cashier can reprint from Order History if customer wants receipt
          setOrderComplete(orderInfo);
          
          setOrderProcessing(false);
          return;
        }
        
        // For cash/card payments, show success immediately
        // For owner orders, show special message
        if (isOwnerOrder) {
          toast.success(`👑 Owner Order #${data.order_number} sent to stations!`, {
            description: 'No charge - food/drinks being prepared',
            duration: 4000
          });
          setCart([]);
          setShowPayment(false);
          setAppliedCashierDiscount(null);
          setIsOwnerOrder(false);  // Reset owner mode after order
          setOrderProcessing(false);
          return;
        }
        
        // Store order info for receipt confirmation
        const orderInfo = {
          order_id: data.order_id || data.id,
          order_number: data.order_number,
          total,
          fiscal_success: data.fiscal_success,
          sdc_number: data.sdc_number,
          is_training: isTrainingMode,
          payment_method: finalPaymentMethod
        };
        
        // Show staff tab notification if applicable
        if (data.staff_tab_added) {
          toast.success(`Staff meal recorded for ${data.staff_member_name}!`, {
            description: `$${total.toFixed(2)} added to payroll deduction`,
            duration: 5000
          });
        }
        
        // Send Thank You to customer display
        sendThankYouToDisplay(total);
        
        // Mark parked sale as completed (if working on a parked sale)
        if (currentParkedSaleId) {
          await completeParkedSale(data.order_id || data.id);
        }
        
        setCart([]);
        setShowPayment(false);
        setAppliedCashierDiscount(null);
        
        // Generate fiscal invoice (background, don't block UI)
        if (!isTrainingMode) {
          fiscalizeItems(cart, total, paymentMethod).catch(fiscalErr => {
            console.error('Fiscal error:', fiscalErr);
          });
        }
        
        // Go directly to order complete screen (no popup, no auto-print)
        // Cashier can reprint from Order History if customer wants receipt
        setOrderComplete(orderInfo);
        
    } catch (err) {
      console.error('Order processing error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Use utility function for clean error extraction
      const errorMsg = extractErrorMessage(err);
      toast.error(`Failed to process order: ${errorMsg}`);
    } finally {
      setOrderProcessing(false);
    }
  };

  // Send order to kitchen (Dine-In) - no payment yet, just creates order with payment_status: "pending"
  // USES SAME ENDPOINT AS SERVER TABLET (/api/orders) since that one works reliably
  const sendToKitchen = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (!tableNumber) {
      toast.error('Please select a table number');
      return;
    }
    
    setOrderProcessing(true);
    try {
      // Separate NEW items from items already sent to kitchen
      const newItems = cart.filter(item => !item.alreadySentToKitchen && !item.fromTableOrder);
      const existingItems = cart.filter(item => item.alreadySentToKitchen || item.fromTableOrder);
      
      // If we have tableOrderIds from loading a table order, use the first order ID
      const existingOrderId = tableOrderIds?.[0] || existingItems[0]?.originalOrderId;
      
      if (existingOrderId) {
        // ========== ADD ONLY NEW ITEMS TO EXISTING ORDER ==========
        if (newItems.length === 0) {
          toast.info('No new items to send - all items already at kitchen');
          setOrderProcessing(false);
          return;
        }
        
        console.log(`POS: Adding ${newItems.length} NEW items to existing order ${existingOrderId} for Table ${tableNumber}`);
        
        const addItemsData = {
          order_id: existingOrderId,
          items: newItems.map(formatCartItemForKitchen)
        };
        
        // Use add-items endpoint - ONLY prints the NEW items
        const res = await api.post('/api/pos/order/add-items', addItemsData);
        const result = res.data;
        
        toast.success(`${result.items_added} items added to Table ${tableNumber}!`, {
          description: `New total: $${result.new_total?.toFixed(2)}`,
          duration: 5000
        });
        
        // Mark new items as sent to kitchen
        setCart(prev => prev.map(item => ({
          ...item,
          alreadySentToKitchen: true,
          originalOrderId: existingOrderId
        })));
        
        // Clear table order IDs as we've processed them
        setTableOrderIds(null);
        
        // Refresh tables
        try {
          const tablesRes = await api.get('/api/tables/active');
          setAvailableTables(tablesRes.data.tables || []);
        } catch (e) {
          // Non-critical
        }
        
        setOrderProcessing(false);
        return;
      }
      
      // CHECK: Is there already an unpaid order for this table that we don't know about?
      let discoveredOrderId = null;
      try {
        const tableOrdersRes = await api.get(`/api/server/table/${tableNumber}/orders`, {
          headers: { 'X-Staff-ID': staff?.id }
        });
        const unpaidOrders = tableOrdersRes.data?.orders?.filter(o => o.payment_status === 'pending') || [];
        if (unpaidOrders.length > 0) {
          discoveredOrderId = unpaidOrders[0].id;
        }
      } catch (e) {
        // No existing orders - will create new
        console.log('No existing table orders found, creating new order');
      }
      
      if (discoveredOrderId) {
        // ========== ADD TO DISCOVERED EXISTING ORDER ==========
        console.log(`POS: Adding ${newItems.length || cart.length} items to discovered order ${discoveredOrderId} for Table ${tableNumber}`);
        
        // Only send new items (not already sent to kitchen)
        const itemsToSend = newItems.length > 0 ? newItems : cart.filter(item => !item.alreadySentToKitchen);
        
        if (itemsToSend.length === 0) {
          toast.info('All items already sent to kitchen');
          setOrderProcessing(false);
          return;
        }
        
        const addItemsData = {
          order_id: discoveredOrderId,
          items: itemsToSend.map(formatCartItemForKitchen)
        };
        
        // Use add-items endpoint - ONLY prints the NEW items
        const res = await api.post('/api/pos/order/add-items', addItemsData);
        const result = res.data;
        
        toast.success(`${result.items_added} items added to Table ${tableNumber}!`, {
          description: `New total: $${result.new_total?.toFixed(2)}`,
          duration: 5000
        });
        
        // Mark parked sale as completed (if working on a parked sale)
        if (currentParkedSaleId) {
          await completeParkedSale(discoveredOrderId);
        }
        
        // Clear cart and reset
        setCart([]);
        setShowPayment(false);
        setAppliedCashierDiscount(null);
        setTableOrderIds(null);
        
        // Refresh tables
        try {
          const tablesRes = await api.get('/api/tables/active');
          setAvailableTables(tablesRes.data.tables || []);
        } catch (e) {
          // Non-critical
        }
        
        setOrderProcessing(false);
        return;
      }
      
      // ========== CREATE NEW ORDER ==========
      // No existing order for this table - create new
      // Build order using SAME structure as Server Tablet
      const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      const orderData = {
        items: cart.map(formatCartItemForKitchen),
        customer_id: customer?.contact_id || customer?.id || null,
        customer_name: isOwnerOrder ? 'OWNER' : (customer?.name || `Table ${tableNumber}`),
        customer_email: customer?.email || null,
        customer_phone: customer?.phone || null,
        subtotal: cartTotal,
        total: cartTotal,
        order_type: 'dine-in',
        table_number: String(tableNumber),
        payment_method: isOwnerOrder ? 'owner' : 'pending',
        payment_status: isOwnerOrder ? 'owner_comp' : 'pending',
        staff_id: staff?.id || null,
        staff_name: staff?.name || null,
        source: 'pos',
        is_owner_order: isOwnerOrder,
        is_training: isTrainingMode
      };
      
      console.log('POS Send to Kitchen - using /api/pos/orders/send-to-kitchen:', orderData);
      
      // Use send-to-kitchen endpoint (matches Server tablet flow)
      const res = await api.post('/api/pos/orders/send-to-kitchen', orderData);
      const data = res.data;
      console.log('Order response:', data);
      
      // Response format: { success, order_id, order_number, message }
      const orderNumber = data.order_number || data.order_id?.slice(0, 8).toUpperCase() || 'NEW';
      
      // Handle owner orders
      if (isOwnerOrder) {
        toast.success(`👑 Owner Order #${orderNumber} sent to Table ${tableNumber}!`, {
          description: 'No charge - food/drinks being prepared',
          duration: 4000
        });
        setCart([]);
        setShowPayment(false);
        setAppliedCashierDiscount(null);
        setIsOwnerOrder(false);
        setOrderProcessing(false);
        return;
      }
      
      toast.success(`Order Sent to Stations!`, {
        description: `Table ${tableNumber} - Order #${orderNumber}`,
        duration: 5000
      });
      
      // Mark parked sale as completed (if working on a parked sale)
      // Note: Even for dine-in, once sent to stations, the parked sale is done
      if (currentParkedSaleId) {
        await completeParkedSale(data.order_id);
      }
      
      // Clear cart and reset state
      setCart([]);
      setShowPayment(false);
      setAppliedCashierDiscount(null);
      setLastOrder({
        order_id: data.order_id,
        order_number: data.order_number,
        table_number: tableNumber,
        total: cartTotal,
        payment_status: 'pending'
      });
      
      // Refresh tables
      try {
        const tablesRes = await api.get('/api/tables/active');
        setAvailableTables(tablesRes.data.tables || []);
      } catch (e) {
        // Non-critical, ignore
      }
      
    } catch (err) {
      console.error('Send to stations error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Properly extract error message - handle object responses
      let errorMsg = 'Network error';
      if (err.response?.data?.detail) {
        errorMsg = typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail);
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.response?.data) {
        errorMsg = typeof err.response.data === 'string'
          ? err.response.data
          : JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      toast.error(`Failed to send: ${errorMsg}`);
    } finally {
      setOrderProcessing(false);
    }
  };

  // M-PAiSA payment polling - check if customer completed payment
  const startMpaisaPolling = (orderId) => {
    setMpaisaPolling(true);
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes at 5-second intervals
    
    const pollInterval = setInterval(async () => {
      attempts++;
      try {
        const res = await api.get(`/api/payments/mpaisa/status/${orderId}`);
        const data = res.data;
        
        if (data.payment?.status === 'completed') {
          clearInterval(pollInterval);
          setMpaisaPolling(false);
          setShowMpaisaPayment(false);
          
          // Show success
          setOrderComplete({
            order_number: lastOrder?.order_number || orderId,
            total: data.payment.amount,
            payment_method: 'mpaisa',
            is_training: isTrainingMode
          });
          
          toast.success('M-PAiSA payment received!');
          
          // Mark parked sale as completed (if working on a parked sale)
          if (currentParkedSaleId) {
            completeParkedSale(orderId).catch(err => {
              console.error('Failed to complete parked sale after M-PAiSA:', err);
            });
          }
          
          // NOTE: Receipt printing is handled by backend's create_print_jobs_for_order()
          // when payment status is updated to 'paid'. No frontend print call needed.
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setMpaisaPolling(false);
          toast.error('Payment timeout - customer did not complete payment');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000); // Poll every 5 seconds
    
    // Store interval ID for cleanup
    return () => clearInterval(pollInterval);
  };

  // Cancel M-PAiSA payment
  const cancelMpaisaPayment = () => {
    setShowMpaisaPayment(false);
    setMpaisaPaymentUrl(null);
    setMpaisaOrderId(null);
    setMpaisaPolling(false);
    toast.info('M-PAiSA payment cancelled');
  };

  // Start new order
  const startNewOrder = () => {
    setOrderComplete(null);
    setCustomer(null);
    setCustomerDiscount(null);
    setAppliedCashierDiscount(null);
    clearFiscalInvoice();
    setShowFiscalReceipt(false);
    // Reset order type state
    setOrderType('takeaway');
    setTableNumber(null);
    setView('customer-lookup');
  };

  // Quick action handlers
  const handleOpenDrawer = async () => {
    if (!staff) return;
    if (!staff.permissions?.can_open_drawer && staff.role === 'staff') {
      toast.error('You do not have permission to open the cash drawer');
      return;
    }
    setQuickActionLoading('drawer');
    try {
      // Send drawer kick command to Windows Print Server
      const response = await api.post('/api/receipts/open-drawer', { 
        staff_id: staff.id, 
        staff_name: staff.name,
        reason: 'manual' 
      });
      if (response.data?.success) {
        toast.success('Cash drawer opening...', {
          description: 'Command sent to printer'
        });
      } else {
        throw new Error('Failed to open drawer');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to open cash drawer');
    } finally {
      setQuickActionLoading(null);
    }
  };

  const handlePrintLastReceipt = async () => {
    if (!lastOrder) {
      toast.error('No previous order to print');
      return;
    }
    setQuickActionLoading('print');
    try {
      // Use the order_id to reprint the receipt via cloud backend
      // This ensures all data (cash received, change, discount, Fiji time) is included
      const orderId = lastOrder.order_id || lastOrder.id;
      
      const response = await api.post('/api/print-jobs', {
        order_id: orderId,
        print_type: 'customer',
        is_reprint: true
      });
      
      if (response.data.success !== false) {
        toast.success('Receipt sent to printer', {
          description: 'Print server will pick it up shortly'
        });
      } else {
        toast.error('Print failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Print receipt error:', err);
      toast.error('Failed to print: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setQuickActionLoading(null);
    }
  };

  // Test print - sends directly from browser to Star printer via StarWebPRNT (HTTPS)
  const handleTestPrint = async () => {
    setQuickActionLoading('testprint');
    try {
      // Call backend to create test print jobs for ALL 5 printers
      // Customer receipt + Kitchen + Barista + Juicer + Counter
      const res = await api.post('/receipts/test-print-all', {
        staff_name: staff?.name || 'POS Staff'
      });
      
      if (res.data.success) {
        toast.success('Test prints sent to ALL printers!', {
          description: `${res.data.jobs?.length || 5} print jobs created: Customer, Kitchen, Barista, Juicer, Counter`,
          duration: 5000
        });
      } else {
        toast.error('Test print failed', {
          description: res.data.message || 'Could not create print jobs'
        });
      }
    } catch (err) {
      console.error('Test print error:', err);
      toast.error('Print failed: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setQuickActionLoading(null);
    }
  };

  // Render based on view state
  if (view === 'login') {
    return <POSPinPad onLogin={handleLogin} loading={loginLoading} />;
  }

  // Open Shift Screen - shown when staff logs in but no active shift
  if (view === 'open-shift') {
    return (
      <POSOpenShiftPrompt
        staff={staff}
        onShiftOpened={(shift) => {
          refreshShift();
          setView('customer-lookup');
        }}
        onSkip={() => setView('customer-lookup')}
      />
    );
  }

  if (view === 'customer-lookup') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <POSTopBar 
          staff={staff} 
          onLogout={handleLogout} 
          onToggleTheme={toggleDarkMode} 
          theme={isDarkMode ? 'dark' : 'light'}
          pendingKioskOrdersCount={pendingKioskOrders.length + paidKioskOrders.length}
          onShowKioskQueue={() => setShowKioskQueue(true)}
          activeShift={activeShift}
          onEndShift={() => setShowCloseShiftDialog(true)}
        />
        <POSCustomerLookup
          onSelectCustomer={handleSelectCustomer}
          onNewCustomer={() => setView('new-customer')}
          onSkip={handleSkipCustomer}
          staffId={staff?.id}
          staffName={staff?.name}
          parkedSalesCount={parkedSales.length}
          onShowParkedSales={() => setShowParkedSales(true)}
        />
        {/* Parked Sales Modal */}
        <POSParkedSalesModal
          isOpen={showParkedSales}
          onClose={() => setShowParkedSales(false)}
          parkedSales={parkedSales}
          onResume={resumeParkedSale}
          onDelete={deleteParkedSale}
          onRelease={releaseParkedSale}
          currentStaffId={staff?.id}
        />
        {/* Close Shift Dialog */}
        {showCloseShiftDialog && activeShift && (
          <POSCloseShiftDialog
            shift={activeShift}
            onClose={() => setShowCloseShiftDialog(false)}
            onShiftClosed={() => {
              setShowCloseShiftDialog(false);
              refreshShift();
              handleLogout();
            }}
          />
        )}
      </div>
    );
  }

  if (view === 'new-customer') {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <POSTopBar 
          staff={staff} 
          onLogout={handleLogout} 
          onToggleTheme={toggleDarkMode} 
          theme={isDarkMode ? 'dark' : 'light'}
          activeShift={activeShift}
          onEndShift={() => setShowCloseShiftDialog(true)}
        />
        <POSNewCustomerForm
          onSave={handleSelectCustomer}
          onCancel={() => setView('customer-lookup')}
        />
        {/* Close Shift Dialog */}
        {showCloseShiftDialog && activeShift && (
          <POSCloseShiftDialog
            shift={activeShift}
            onClose={() => setShowCloseShiftDialog(false)}
            onShiftClosed={() => {
              setShowCloseShiftDialog(false);
              refreshShift();
              handleLogout();
            }}
          />
        )}
      </div>
    );
  }

  if (showRefundFlow) {
    return <POSRefundFlow onClose={() => setShowRefundFlow(false)} cashierName={staff?.name || 'Admin'} />;
  }

  if (showVoidInvoice) {
    return <POSVoidInvoice onClose={() => setShowVoidInvoice(false)} cashierName={staff?.name || 'Admin'} />;
  }

  if (orderComplete) {
    return (
      <POSOrderCompleteScreen
        orderComplete={orderComplete}
        fiscalInvoice={fiscalInvoice}
        showFiscalReceipt={showFiscalReceipt}
        setShowFiscalReceipt={setShowFiscalReceipt}
        onStartNewOrder={startNewOrder}
      />
    );
  }

  // Main ordering view - categories from menu items (POSMenuGrid handles "All" button)
  const categoryList = categories.map(c => ({ id: c, name: c, is_active: true }));

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col" data-testid="pos-ordering-screen">
      {/* Owner Order Mode Banner */}
      {isOwnerOrder && (
        <div className="bg-teal-600 text-white text-center py-2 px-4 flex items-center justify-center gap-2">
          <span className="text-xl">👑</span>
          <span className="font-bold">OWNER ORDER</span>
          <span className="text-teal-100">- No charge, prints to all stations</span>
        </div>
      )}
      
      {/* Training Mode Banner */}
      {isTrainingMode && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 flex items-center justify-center gap-2">
          <GraduationCap className="w-5 h-5" />
          <span className="font-bold">TRAINING MODE</span>
          <span className="text-amber-100">- Invoices will NOT be sent to FRCS</span>
        </div>
      )}
      
      {/* Top Bar */}
      <POSTopBar 
        staff={staff} 
        onLogout={handleLogout} 
        onToggleTheme={toggleDarkMode} 
        theme={isDarkMode ? 'dark' : 'light'}
        pendingKioskOrdersCount={pendingKioskOrders.length}
        onShowKioskQueue={() => setShowKioskQueue(true)}
        activeShift={activeShift}
        onEndShift={() => setShowCloseShiftDialog(true)}
      />

      {/* Action Bar - NOW AT TOP (Home, Split Check, History, Park Sale, Clear, Parked) */}
      <POSActionBar
        cartCount={cart.length}
        parkedSalesCount={parkedSales.length}
        onGoHome={goToHome}
        onParkSale={parkSale}
        onClearCart={clearCart}
        onShowParkedSales={() => setShowParkedSales(true)}
        onSplitCheck={() => setShowTableBillLookup(true)}
        onOrderHistory={() => setShowOrderHistory(true)}
        disabled={orderProcessing}
      />

      {/* Unified Incoming Orders Panel - Phone, Kiosk, WhatsApp, Online */}
      <POSIncomingOrders 
        staffId={staff?.id}
        onOrderAccepted={(order) => {
          toast.success(`Order #${order.order_number} sent to preparation!`);
          // Refresh kiosk orders too
          fetchPendingKioskOrders();
        }}
      />

      {/* Print Failure Alert - Shows popup when prints fail */}
      <POSPrintFailureAlert />

      {/* Order Type Selector (Phase 10) */}
      <POSOrderTypeSelector
        orderType={orderType}
        setOrderType={setOrderType}
        tableNumber={tableNumber}
        setTableNumber={setTableNumber}
        tables={availableTables}
        disabled={cart.length > 0}
      />

      {/* Customer Info Bar */}
      {customer && (
        <div className="bg-teal-50 dark:bg-teal-900/20 border-b border-teal-200 dark:border-teal-800 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-teal-700 dark:text-teal-300">
                Customer: <span className="font-medium">{customer.name || `${customer.first_name} ${customer.last_name || ''}`}</span>
              </span>
              {customerDiscount && (
                <span className="px-2 py-0.5 bg-teal-600 text-white text-xs rounded-full">
                  {customerDiscount.discount}% {customerDiscount.tier || 'Loyalty'} Discount
                </span>
              )}
              {/* Coffee Pass Status - NEW: Direct to menu selection, not modal */}
              {customerCoffeePass?.has_active_pass && (
                <div className="flex flex-col gap-1">
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium flex items-center gap-1 ${
                    customerCoffeePass.can_redeem_now 
                      ? 'bg-green-500 text-white animate-pulse' 
                      : customerCoffeePass.already_redeemed_today
                      ? 'bg-amber-500 text-white'
                      : 'bg-blue-500 text-white'
                  }`}>
                    <Coffee className="w-3 h-3" />
                    {customerCoffeePass.can_redeem_now 
                      ? 'FREE COFFEE READY' 
                      : customerCoffeePass.already_redeemed_today
                      ? 'Redeemed Today'
                      : 'Bula Pass'}
                  </span>
                  {customerCoffeePass.can_redeem_now && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      Select coffee from menu - 8oz FREE
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCustomer(null); setCustomerDiscount(null); setCustomerCoffeePass(null); setView('customer-lookup'); }}
                className="text-teal-600 dark:text-teal-400"
              >
                Change Customer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Menu Grid - Full width on mobile */}
        <POSMenuGrid
          menuItems={menuItems}
          categories={categoryList}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          onItemClick={handleItemClick}
          search={search}
          setSearch={setSearch}
        />

        {/* Cart - Hidden on mobile, shown on md+ */}
        <div className="hidden md:block">
          <POSCart
            cart={cart}
            customer={customer}
            customerDiscount={customerDiscount}
            appliedCashierDiscount={appliedCashierDiscount}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeItem}
            onClearCart={clearCart}
            onAddItem={addToCart}
            showPayment={showPayment}
            setShowPayment={setShowPayment}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            onProcessOrder={processOrder}
            orderProcessing={orderProcessing}
            onShowCashierDiscount={() => setShowCashierDiscountModal(true)}
            onRemoveCashierDiscount={() => setAppliedCashierDiscount(null)}
            upsellReminderCollapsed={upsellReminderCollapsed}
            setUpsellReminderCollapsed={setUpsellReminderCollapsed}
            orderType={orderType}
            tableNumber={tableNumber}
            staff={staff}
            onCompItem={(itemId, managerName, managerRole) => {
              setCart(prev => prev.map(item => 
                item.item_id === itemId 
                  ? { ...item, isComped: true, compedBy: managerName, compedByRole: managerRole }
                  : item
              ));
            }}
            onVoidItem={(itemId, managerName, managerRole) => {
              console.log(`Item ${itemId} voided by ${managerName} (${managerRole})`);
              removeItem(itemId);
            }}
            onApplyItemDiscount={(itemId, percent, managerName, managerRole) => {
              setCart(prev => prev.map(item => 
                item.item_id === itemId 
                  ? { ...item, discountPercent: percent, dollarDiscount: 0, discountBy: managerName, discountByRole: managerRole }
                  : item
              ));
            }}
            onApplyItemDollarDiscount={(itemId, amount, managerName, managerRole) => {
              setCart(prev => prev.map(item => 
                item.item_id === itemId 
                  ? { ...item, dollarDiscount: amount, discountPercent: 0, discountBy: managerName, discountByRole: managerRole }
                  : item
              ));
            }}
            onStaffDiscount={() => setShowStaffOrder(true)}
            onFamilyFriendsDiscount={() => setShowFFDiscount(true)}
            onSendToKitchen={sendToKitchen}
            isOwnerOrder={isOwnerOrder}
          />
        </div>

        {/* Mobile Cart Drawer - Only render POSCart when drawer is open to reduce DOM */}
        <POSMobileCartDrawer
          isOpen={mobileCartOpen}
          onToggle={() => setMobileCartOpen(!mobileCartOpen)}
          itemCount={cart.reduce((sum, item) => sum + item.quantity, 0)}
          total={cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)}
        >
          {mobileCartOpen && (
            <POSCart
              cart={cart}
              customer={customer}
              customerDiscount={customerDiscount}
              appliedCashierDiscount={appliedCashierDiscount}
              onUpdateQuantity={updateQuantity}
              onRemoveItem={removeItem}
              onClearCart={clearCart}
              onAddItem={addToCart}
              showPayment={showPayment}
              setShowPayment={setShowPayment}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              onProcessOrder={processOrder}
              orderProcessing={orderProcessing}
              onShowCashierDiscount={() => setShowCashierDiscountModal(true)}
              onRemoveCashierDiscount={() => setAppliedCashierDiscount(null)}
              upsellReminderCollapsed={upsellReminderCollapsed}
              setUpsellReminderCollapsed={setUpsellReminderCollapsed}
              orderType={orderType}
              tableNumber={tableNumber}
              staff={staff}
              onCompItem={(itemId, managerName, managerRole) => {
                setCart(prev => prev.map(item => 
                  item.item_id === itemId 
                    ? { ...item, isComped: true, compedBy: managerName, compedByRole: managerRole }
                    : item
                ));
              }}
              onVoidItem={(itemId, managerName, managerRole) => {
                console.log(`Item ${itemId} voided by ${managerName} (${managerRole})`);
                removeItem(itemId);
              }}
              onApplyItemDiscount={(itemId, percent, managerName, managerRole) => {
                setCart(prev => prev.map(item => 
                  item.item_id === itemId 
                    ? { ...item, discountPercent: percent, dollarDiscount: 0, discountBy: managerName, discountByRole: managerRole }
                    : item
                ));
              }}
              onApplyItemDollarDiscount={(itemId, amount, managerName, managerRole) => {
                setCart(prev => prev.map(item => 
                  item.item_id === itemId 
                    ? { ...item, dollarDiscount: amount, discountPercent: 0, discountBy: managerName, discountByRole: managerRole }
                    : item
                ));
              }}
              onStaffDiscount={() => setShowStaffOrder(true)}
              onFamilyFriendsDiscount={() => setShowFFDiscount(true)}
              onSendToKitchen={sendToKitchen}
              isMobileDrawer={true}
              isOwnerOrder={isOwnerOrder}
            />
          )}
        </POSMobileCartDrawer>
      </div>

      {/* Quick Actions */}
      <POSQuickActions
        showQuickActions={showQuickActions}
        setShowQuickActions={setShowQuickActions}
        quickActionLoading={quickActionLoading}
        onOpenDrawer={handleOpenDrawer}
        onPrintLastReceipt={handlePrintLastReceipt}
        onTestPrint={handleTestPrint}
        onVoidInvoice={() => setShowVoidInvoice(true)}
        onRefund={() => setShowRefundFlow(true)}
        onAdvanceSale={() => toast.info('Advance sale: Coming soon')}
        onProformaSale={() => toast.info('Proforma: Coming soon')}
        isTrainingMode={isTrainingMode}
        onToggleTrainingMode={() => setIsTrainingMode(!isTrainingMode)}
        isOwnerOrder={isOwnerOrder}
        onToggleOwnerOrder={() => {
          setIsOwnerOrder(!isOwnerOrder);
          if (!isOwnerOrder) {
            toast.success('👑 Owner Mode ON - Orders will print to stations with no charge', { duration: 3000 });
          } else {
            toast.info('Owner Mode OFF - Back to normal orders');
          }
        }}
        lastOrder={lastOrder}
        staff={staff}
        onFindTableBill={() => setShowTableBillLookup(true)}
        onStaffOrder={() => setShowStaffOrder(true)}
        onPromoCode={() => setShowPromoCode(true)}
        onLogMistake={() => setShowLogMistake(true)}
        onKioskOrders={() => setShowKioskQueue(true)}
        onFamilyFriendsDiscount={() => cart.length > 0 ? setShowFFDiscount(true) : toast.error('Add items to cart first')}
        kioskOrderCount={pendingKioskOrders.length + paidKioskOrders.length}
      />

      {/* Item Customization Modal */}
      {selectedItem && (
        <ItemCustomizationModal
          item={selectedItem}
          open={showCustomization}
          onOpenChange={(isOpen) => { 
            setShowCustomization(isOpen); 
            if (!isOpen) setSelectedItem(null); 
          }}
          onAddToCart={(cartItem) => {
            // ItemCustomizationModal passes a complete cartItem object
            setCart(prev => {
              const existing = prev.find(c => c.item_id === cartItem.item_id && !cartItem.modifiers);
              if (existing && !cartItem.modifiers) {
                return prev.map(c => c.item_id === cartItem.item_id ? { ...c, quantity: c.quantity + cartItem.quantity } : c);
              }
              return [...prev, {
                ...cartItem,
                item_id: cartItem.item_id + (cartItem.selectedSize ? `-${cartItem.selectedSize}` : '') + (cartItem.modifiers ? `-${Date.now()}` : ''),
                name: cartItem.name + (cartItem.selectedSize ? ` (${cartItem.selectedSize})` : ''),
              }];
            });
            setShowCustomization(false);
            setSelectedItem(null);
          }}
          availableAddOns={availableAddOns}
          coffeePassStatus={
            // Pass status if can redeem OR can upgrade to redeem, AND cart doesn't already have a redemption
            (customerCoffeePass?.can_redeem_now || customerCoffeePass?.can_upgrade_to_redeem) && 
            !cart.some(item => item.isCoffeePassRedemption || item.isCoffeeClubRedemption || item.is_coffee_pass_redemption)
              ? customerCoffeePass 
              : null
          }
          onCoffeePassRedeemed={(data) => {
            // Refresh coffee pass status after redemption
            if (customer?.email || customer?.phone) {
              // The status will be updated when the item is added to cart
              setCustomerCoffeePass(prev => prev ? {...prev, can_redeem_now: false, already_redeemed_today: true} : null);
            }
          }}
        />
      )}

      {/* Close Shift Dialog */}
      {showCloseShiftDialog && activeShift && (
        <POSCloseShiftDialog
          shift={activeShift}
          onClose={() => setShowCloseShiftDialog(false)}
          onShiftClosed={() => {
            setShowCloseShiftDialog(false);
            refreshShift();
            handleLogout();
          }}
        />
      )}

      {/* M-PAiSA Payment Modal */}
      {showMpaisaPayment && mpaisaPaymentUrl && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">M-PAiSA Payment</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                Customer can pay using M-PAiSA mobile wallet
              </p>
            </div>

            {/* QR Code Placeholder - Customer scans this */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-4">
              <div className="bg-white p-4 rounded-lg mx-auto max-w-[200px]">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mpaisaPaymentUrl)}`}
                  alt="M-PAiSA Payment QR Code"
                  className="w-full"
                />
              </div>
              <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-3">
                Scan with M-PAiSA app to pay
              </p>
            </div>

            {/* Or send link option */}
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
              <p>Or share payment link with customer:</p>
              <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono break-all">
                {mpaisaPaymentUrl}
              </div>
            </div>

            {/* Status indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {mpaisaPolling ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span className="text-sm text-blue-600 dark:text-blue-400">Waiting for payment...</span>
                </>
              ) : (
                <span className="text-sm text-gray-500">Payment link ready</span>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={cancelMpaisaPayment}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => window.open(mpaisaPaymentUrl, '_blank')}
              >
                Open Payment Page
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table Bill Lookup Modal */}
      {showTableBillLookup && (
        <POSTableBillLookup
          staffId={staff?.id}
          staffName={staff?.name}
          onPayBill={(result) => {
            toast.success(`Table ${result.table_number} paid: $${result.total_amount?.toFixed(2) || result.amount_paid?.toFixed(2)}`);
          }}
          onClose={() => setShowTableBillLookup(false)}
          onAddItems={(tableData) => {
            // Load table order into POS cart for adding more items
            // Mark existing items as "fromTableOrder" so we know NOT to reprint/resend them
            const cartItems = (tableData.items || []).map((item, idx) => ({
              item_id: item.item_id || `table-item-${idx}-${Date.now()}`,
              name: item.name,
              price: item.price,
              quantity: item.quantity || 1,
              category: item.category,
              size: item.size,
              customizations: item.customizations || [],
              add_ons: item.add_ons || [],
              special_instructions: item.special_instructions,
              modifiers: item.modifiers || [],
              // Mark as existing table order item - will NOT be sent to kitchen again
              fromTableOrder: true,
              originalOrderId: tableData.orderId,
              alreadySentToKitchen: true  // Prevents reprinting
            }));
            
            setCart(cartItems);
            setOrderType('dine-in');
            setTableNumber(tableData.tableNumber.toString());
            
            // Set customer if available
            if (tableData.customer) {
              setCustomer({
                name: tableData.customer.customer_name,
                email: tableData.customer.customer_email,
                phone: tableData.customer.customer_phone,
                id: tableData.customer.customer_id
              });
            }
            
            // Track the existing table order IDs for payment processing
            setCurrentParkedSaleId(null); // Clear any parked sale tracking
            setTableOrderIds(tableData.orderIds);
            
            // Go to ordering view to allow adding more items
            setView('ordering');
            setShowTableBillLookup(false);
            
            toast.info(`Table ${tableData.tableNumber} loaded with ${cartItems.length} items`, {
              description: 'Add items from the menu, then Send to Kitchen or Process Payment',
              duration: 5000
            });
          }}
        />
      )}

      {/* Staff Order Modal */}
      {showStaffOrder && (
        <POSStaffDiscount
          cartItems={cart}
          cartSubtotal={cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)}
          staffId={staff?.id}
          onClose={() => setShowStaffOrder(false)}
          onComplete={(result) => {
            if (result.paymentMethod === 'add_to_tab') {
              // Added to tab - clear cart and show success
              toast.success(`Added to ${result.staffMember.name}'s tab: $${result.discountedTotal.toFixed(2)}`);
              setCart([]);
              setShowStaffOrder(false);
            } else {
              // Other payment - apply discount to cart and proceed
              setAppliedCashierDiscount({
                type: 'staff_50',
                percent: 50,
                amount: result.discountAmount,
                staffName: result.staffMember.name,
                staffId: result.staffMember.id,
                label: result.discountLabel
              });
              setShowStaffOrder(false);
              // Show payment options
              toast.success(`50% Staff Discount Applied for ${result.staffMember.name}`, {
                description: `Pay $${result.discountedTotal.toFixed(2)} with ${result.paymentMethod}`
              });
            }
          }}
        />
      )}

      {/* Promo Code Modal */}
      {showPromoCode && (
        <POSPromoCode
          subtotal={cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)}
          customerId={customer?.contact_id}
          onApplyDiscount={(promo) => {
            setAppliedPromoCode(promo);
            // For percentage discounts, only set 'percent' (POSPage calculates amount)
            // For fixed dollar discounts, only set 'amount'
            // Never set BOTH to avoid double-counting
            setAppliedCashierDiscount({
              type: 'promo',
              code: promo.code,
              percent: promo.discountType === 'percent' ? promo.discountValue : null,
              amount: promo.discountType === 'fixed' ? promo.calculatedDiscount : null
            });
            toast.success(`Promo code ${promo.code} applied!`);
          }}
          onClose={() => setShowPromoCode(false)}
        />
      )}

      {/* Log Mistake Modal */}
      {showLogMistake && (
        <POSLogMistake
          staffId={staff?.id}
          onClose={() => setShowLogMistake(false)}
        />
      )}

      {/* Family & Friends Discount Modal */}
      {showFFDiscount && (
        <POSFamilyFriendsDiscount
          cart={cart}
          subtotal={cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)}
          staffId={staff?.id}
          staffName={staff?.name}
          onClose={() => setShowFFDiscount(false)}
          onApplyDiscount={(discountInfo) => {
            setAppliedFFDiscount(discountInfo);
            // IMPORTANT: For percentage discounts, only pass 'percent' - never both percent AND amount
            // POSPage.jsx calculates the dollar amount from percent to avoid double-counting
            setAppliedCashierDiscount({
              type: 'family_friends',
              percent: discountInfo.percent,
              // 'amount' is intentionally omitted for percentage discounts
              approvedBy: discountInfo.approvedBy
            });
            toast.success(`15% Family & Friends discount applied!`, {
              description: `For ${discountInfo.customerName} (${discountInfo.relationship})`
            });
          }}
        />
      )}

      {/* Cashier Discount Modal with Manager Override */}
      {showCashierDiscountModal && (
        <POSCashierDiscountModal
          isOpen={showCashierDiscountModal}
          onClose={() => setShowCashierDiscountModal(false)}
          subtotal={cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)}
          staffId={staff?.id}
          staffName={staff?.name}
          onApplyDiscount={(discountInfo) => {
            // IMPORTANT: For percentage discounts, only pass 'percent' - never both percent AND amount
            // POSPage.jsx calculates the dollar amount from percent to avoid double-counting
            // 'amount' should ONLY be set for fixed dollar discounts (e.g., "$5 off coupon")
            setAppliedCashierDiscount({
              type: 'cashier',
              percent: discountInfo.percent,
              // 'amount' is intentionally omitted for percentage discounts
              approvedBy: discountInfo.approvedBy,
              approvedByRole: discountInfo.approvedByRole
            });
            setShowCashierDiscountModal(false);
            toast.success(`${discountInfo.percent}% discount applied!`, {
              description: `Approved by ${discountInfo.approvedBy}`
            });
          }}
        />
      )}

      {/* Kiosk Cash Orders Queue Modal */}
      {showKioskQueue && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-cyan-600 dark:bg-cyan-700 text-white p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Bell className="w-6 h-6" />
                Kiosk Orders - Cash Payment
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKioskQueue(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Orders List */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {pendingKioskOrders.length === 0 && paidKioskOrders.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No pending kiosk orders</p>
                  <p className="text-sm">Orders will appear here when customers order at the kiosk</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* M-PAiSA Paid Orders - Need Confirmation */}
                  {paidKioskOrders.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        M-PAiSA PAID - Confirm to Print ({paidKioskOrders.length})
                      </h4>
                      {paidKioskOrders.map((order) => (
                        <div
                          key={order.id}
                          className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800 mb-3"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                                  #{order.order_number || order.id?.slice(0, 8).toUpperCase()}
                                </span>
                                <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-0.5 rounded text-sm font-medium flex items-center gap-1">
                                  <Check className="w-3 h-3" /> M-PAiSA PAID
                                </span>
                              </div>
                              <p className="text-gray-600 dark:text-gray-400 mt-1">
                                {order.customer_name || 'Kiosk Guest'}
                                {order.customer_phone && (
                                  <span className="ml-2 text-sm">📞 {order.customer_phone}</span>
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                ${order.total?.toFixed(2) || '0.00'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {order.item_count || order.items?.length || 0} items
                              </p>
                            </div>
                          </div>
                          
                          {/* Items Preview */}
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 bg-white/50 dark:bg-gray-800/50 rounded-lg p-2">
                            {(order.items || []).slice(0, 3).map((item, idx) => (
                              <div key={idx}>{item.quantity || 1}x {item.name}</div>
                            ))}
                            {(order.items || []).length > 3 && (
                              <div className="text-gray-400">+{order.items.length - 3} more items</div>
                            )}
                          </div>
                          
                          {/* Confirm Button - Triggers print to all printers */}
                          <Button
                            onClick={async () => {
                              try {
                                const res = await api.post(`/api/orders/kiosk-confirm/${order.id}`);
                                if (res.data.success) {
                                  toast.success(`Order #${order.order_number || order.id?.slice(0, 8)} confirmed!`, {
                                    description: 'Receipt + station tickets sent to printers'
                                  });
                                  fetchPendingKioskOrders();
                                }
                              } catch (err) {
                                toast.error('Failed to confirm: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                            data-testid="kiosk-confirm-mpaisa-btn"
                          >
                            <Check className="w-5 h-5 mr-2" />
                            Confirm & Print to All Stations
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Cash Pending Orders - Need Checkout */}
                  {pendingKioskOrders.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        CASH PENDING - Checkout Required ({pendingKioskOrders.length})
                      </h4>
                      {pendingKioskOrders.map((order) => (
                    <div
                      key={order.id}
                          className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-700 mb-3"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                              #{order.order_number || order.id?.slice(0, 8).toUpperCase()}
                            </span>
                            <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded text-sm font-medium">
                              Cash Pending
                            </span>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 mt-1">
                            {order.customer_name || 'Kiosk Guest'}
                            {order.customer_phone && (
                              <span className="ml-2 text-sm">📞 {order.customer_phone}</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            ${order.total?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {order.item_count || order.items?.length || 0} items
                          </p>
                        </div>
                      </div>
                      
                      {/* Items Preview */}
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {(order.items || []).slice(0, 3).map((item, idx) => (
                          <span key={idx}>
                            {idx > 0 && ', '}
                            {item.quantity || 1}x {item.name}
                          </span>
                        ))}
                        {(order.items || []).length > 3 && (
                          <span className="text-gray-400"> +{order.items.length - 3} more</span>
                        )}
                      </div>
                      
                      {/* Checkout Button - Opens Cash Payment Dialog */}
                      <Button
                        onClick={() => {
                          setKioskCheckoutOrder(order);
                          setKioskCashReceived('');
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                        data-testid="kiosk-checkout-btn"
                      >
                        <Check className="w-5 h-5 mr-2" />
                        Checkout - ${order.total?.toFixed(2)}
                      </Button>
                    </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Kiosk Cash Checkout Modal */}
      {kioskCheckoutOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">Cash Checkout</h3>
                  <p className="text-green-100">Order #{kioskCheckoutOrder.order_number || kioskCheckoutOrder.id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setKioskCheckoutOrder(null)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Customer: {kioskCheckoutOrder.customer_name || 'Kiosk Guest'}
              </p>
              <div className="space-y-1 text-sm max-h-32 overflow-y-auto">
                {(kioskCheckoutOrder.items || []).map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span>{item.quantity || 1}x {item.name}</span>
                    <span>${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${kioskCheckoutOrder.subtotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>VAT (12.5%)</span>
                  <span>${kioskCheckoutOrder.vat_amount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between font-bold text-lg mt-1">
                  <span>TOTAL</span>
                  <span className="text-green-600">${kioskCheckoutOrder.total?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </div>
            
            {/* Cash Entry */}
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cash Received from Customer
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={kioskCashReceived}
                  onChange={(e) => setKioskCashReceived(e.target.value)}
                  placeholder="0.00"
                  className="pl-8 text-2xl h-14 text-center font-bold"
                  autoFocus
                  data-testid="kiosk-cash-input"
                />
              </div>
              
              {/* Quick Cash Buttons */}
              <div className="grid grid-cols-4 gap-2 mt-3">
                {[5, 10, 20, 50].map((amount) => (
                  <Button
                    key={amount}
                    variant="outline"
                    onClick={() => setKioskCashReceived(amount.toString())}
                    className="h-12 font-bold"
                  >
                    ${amount}
                  </Button>
                ))}
              </div>
              
              {/* Change Display */}
              {kioskCashReceived && parseFloat(kioskCashReceived) >= (kioskCheckoutOrder.total || 0) && (
                <div className="mt-4 bg-green-50 dark:bg-green-900/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-700 dark:text-green-300">Change to Give Back</p>
                  <p className="text-4xl font-bold text-green-600 dark:text-green-400">
                    ${(parseFloat(kioskCashReceived) - (kioskCheckoutOrder.total || 0)).toFixed(2)}
                  </p>
                </div>
              )}
              
              {kioskCashReceived && parseFloat(kioskCashReceived) < (kioskCheckoutOrder.total || 0) && (
                <div className="mt-4 bg-red-50 dark:bg-red-900/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-700 dark:text-red-300">Amount Short</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    ${((kioskCheckoutOrder.total || 0) - parseFloat(kioskCashReceived)).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl flex gap-3">
              <Button
                variant="outline"
                onClick={() => setKioskCheckoutOrder(null)}
                className="flex-1"
                disabled={kioskCheckoutProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const cashAmount = parseFloat(kioskCashReceived) || 0;
                  if (cashAmount < (kioskCheckoutOrder.total || 0)) {
                    toast.error('Insufficient cash received');
                    return;
                  }
                  
                  setKioskCheckoutProcessing(true);
                  try {
                    const res = await api.post(`/api/orders/kiosk-mark-paid/${kioskCheckoutOrder.id}`, {
                      payment_method: 'cash',
                      cash_received: cashAmount,
                      change_given: cashAmount - (kioskCheckoutOrder.total || 0)
                    });
                    
                    if (res.data.success) {
                      const change = (cashAmount - (kioskCheckoutOrder.total || 0)).toFixed(2);
                      toast.success(`Payment Complete! Give $${change} change`, {
                        description: `Order #${kioskCheckoutOrder.order_number || kioskCheckoutOrder.id?.slice(0, 8)} sent to printers`,
                        duration: 5000
                      });
                      setKioskCheckoutOrder(null);
                      fetchPendingKioskOrders();
                    }
                  } catch (err) {
                    toast.error('Payment failed: ' + (err.response?.data?.detail || err.message));
                  } finally {
                    setKioskCheckoutProcessing(false);
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={kioskCheckoutProcessing || !kioskCashReceived || parseFloat(kioskCashReceived) < (kioskCheckoutOrder.total || 0)}
                data-testid="kiosk-complete-payment-btn"
              >
                {kioskCheckoutProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Check className="w-5 h-5 mr-2" />
                )}
                Complete Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Parked Sales Modal */}
      <POSParkedSalesModal
        isOpen={showParkedSales}
        onClose={() => setShowParkedSales(false)}
        parkedSales={parkedSales}
        onResume={resumeParkedSale}
        onDelete={deleteParkedSale}
        onRelease={releaseParkedSale}
        currentStaffId={staff?.id}
      />

      {/* Order History Modal (2-day lookback) */}
      {showOrderHistory && (
        <POSOrderHistory
          staffId={staff?.id}
          onClose={() => setShowOrderHistory(false)}
        />
      )}
    </div>
  );
}
